import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { colors } from "utils";
import { ChoiceDropdown } from "./ChoiceDropdown";
import { Definitions } from "./Definitions";
import { isComplete, lastDays, rangeProblem } from "./dateRange";
import { easternToday } from "./easternTime";
import { SegmentedControl } from "./SegmentedControl";
import { SessionsTable } from "./SessionsTable";
import { ErrorState, LoadingState } from "./SectionStates";
import { BORDER, Card, CRITICAL_RED, WHITE } from "./styles";
import { fetchSessions, fetchWaitTimes } from "./teacherReportsMetricsApi";
import { useLoad } from "./useLoad";
import { WaitChart } from "./WaitChart";
import { WaitFigures } from "./WaitFigures";

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const Top = styled.div`
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
`;

const Heading = styled.h1`
  color: ${colors.nightlyWoods500};
  font-size: 22px;
  font-weight: 500;
  margin: 0;
`;

const Lede = styled.p`
  color: ${colors.milkGlass950};
  font-size: 13px;
  margin: 2px 0 0;
`;

const Controls = styled.div`
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const DateField = styled.label`
  align-items: center;
  color: ${colors.milkGlass950};
  display: inline-flex;
  font-size: 12px;
  font-weight: 600;
  gap: 6px;
  margin: 0;
  text-transform: uppercase;
`;

const DateInput = styled.input`
  background: ${WHITE};
  border: 1px solid ${BORDER};
  border-radius: 8px;
  color: ${colors.nightlyWoods950};
  font-size: 13px;
  font-weight: 400;
  padding: 6px 8px;
`;

const RangeMessage = styled.p`
  color: ${CRITICAL_RED};
  font-size: 13px;
  margin: -8px 0 0;
  text-align: right;
`;

const PRESETS = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" }
];
const INITIAL_PRESET_DAYS = 30;

const VIEWS = [
  { granularity: "day", label: "By day" },
  { granularity: "week", label: "By week" }
];
const INITIAL_GRANULARITY = "week";
const MEASUREMENTS = [
  { value: "user_wait", label: "User Wait" },
  { value: "report_generation", label: "Report Generation" }
];
const REPORTS = [
  { value: "all", label: "All" },
  { value: "first", label: "First" }
];
const USER_WAIT = MEASUREMENTS[0].value;
const NO_SESSIONS = [];
const withReportJustStarted = (session) => ({
  ...session,
  status: "processing",
  status_detail: null,
  is_stuck: false,
  needs_attention: session.is_late
});

export const TeacherReportsMetrics = () => {
  const [presetDays, setPresetDays] = useState(INITIAL_PRESET_DAYS);
  const [range, setRange] = useState(() => lastDays(INITIAL_PRESET_DAYS));
  const [fields, setFields] = useState(range);
  const [rangeMessage, setRangeMessage] = useState(null);
  const [granularity, setGranularity] = useState(INITIAL_GRANULARITY);
  const [measurement, setMeasurement] = useState(USER_WAIT);
  const [reports, setReports] = useState(REPORTS[0].value);
  const [waitTimes, loadWaitTimes] = useLoad(fetchWaitTimes);
  const [sessions, loadSessions, updateSessions] = useLoad(fetchSessions);
  const today = easternToday();

  const loadFigures = (changes) => loadWaitTimes({ ...range, granularity, measurement, reports, ...changes });

  const loadRange = (nextRange) => {
    loadFigures(nextRange);
    loadSessions(nextRange);
  };

  useEffect(() => {
    loadRange(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choosePreset = ({ days }) => {
    const nextRange = lastDays(days);
    setPresetDays(days);
    setFields(nextRange);
    setRangeMessage(null);
    setRange(nextRange);
    loadRange(nextRange);
  };

  const editRange = (edge) => (event) => {
    const typedRange = { ...fields, [edge]: event.target.value };
    setFields(typedRange);
    if (!isComplete(typedRange)) return;
    const problem = rangeProblem(typedRange);
    setPresetDays(null);
    setRangeMessage(problem);
    if (problem) return;
    setRange(typedRange);
    loadRange(typedRange);
  };

  const markReportStarted = (sessionId) => {
    updateSessions((loaded) => ({
      ...loaded,
      sessions: loaded.sessions.map((session) =>
        session.session_id === sessionId ? withReportJustStarted(session) : session
      )
    }));
  };

  const chooseView = (option) => {
    setGranularity(option.granularity);
    loadFigures({ granularity: option.granularity });
  };

  const chooseMeasurement = (chosen) => {
    setMeasurement(chosen);
    loadFigures({ measurement: chosen });
  };

  const chooseReports = (chosen) => {
    setReports(chosen);
    loadFigures({ reports: chosen });
  };

  const measuresUserWait = measurement === USER_WAIT;

  return (
    <Page>
      <Top>
        <div>
          <Heading>Teacher Reports Metrics</Heading>
          <Lede>How long teachers wait for the session report after a session ends.</Lede>
        </div>
        <Controls>
          <SegmentedControl
            label="Date range"
            options={PRESETS}
            isSelected={(option) => option.days === presetDays}
            onChoose={choosePreset}
          />
          <DateField>
            From
            <DateInput type="date" value={fields.startDate} max={today} onChange={editRange("startDate")} />
          </DateField>
          <DateField>
            To
            <DateInput type="date" value={fields.endDate} max={today} onChange={editRange("endDate")} />
          </DateField>
          <SegmentedControl
            label="View"
            options={VIEWS}
            isSelected={(option) => option.granularity === granularity}
            onChoose={chooseView}
          />
          <ChoiceDropdown
            id="teacher-reports-metrics-measurement"
            label="Measurement"
            options={MEASUREMENTS}
            value={measurement}
            onChoose={chooseMeasurement}
          />
          <ChoiceDropdown
            id="teacher-reports-metrics-report-count"
            label="Report Count"
            options={REPORTS}
            value={reports}
            onChoose={chooseReports}
            disabled={measuresUserWait}
            note={measuresUserWait ? "User Wait is measured once per session." : null}
          />
        </Controls>
      </Top>
      {rangeMessage && <RangeMessage role="alert">{rangeMessage}</RangeMessage>}
      {waitTimes.status === "loading" && (
        <Card>
          <LoadingState />
        </Card>
      )}
      {waitTimes.status === "error" && (
        <Card>
          <ErrorState onRetry={() => loadFigures()} />
        </Card>
      )}
      {waitTimes.status === "ready" && (
        <>
          <WaitFigures summary={waitTimes.data.summary} measurement={measurement} reports={reports} />
          <WaitChart
            granularity={granularity}
            measurement={measurement}
            buckets={waitTimes.data.buckets}
            hasMeasurements={waitTimes.data.summary.measurement_count > 0}
          />
        </>
      )}
      <SessionsTable
        status={sessions.status}
        sessions={sessions.data?.sessions || NO_SESSIONS}
        asOf={sessions.data?.as_of}
        onRetry={() => loadSessions(range)}
        onReportStarted={markReportStarted}
      />
      <Definitions />
    </Page>
  );
};
